import { AnyAction, isFulfilled, isPending, isRejected, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { AnyApiAction, Arg, RejectedAction } from '@flexible-reports/store/slices/metrics/metrics-types';
import { TaktApiMapConfig } from '@flexible-reports/hooks/takt-logger/takt-map-config';
import { ApiMapConfigKeys, TAKT_NAMESPACE_FOR_FRB, TaktLoggerProps } from '@flexible-reports/hooks/takt-logger/types';
import { whenAmazonTakt } from '@amzn/takt-utils';
import { logger } from '@flexible-reports/services/logger';
import { buildApiFailureTaktPayload, buildApiSuccessTaktPayload } from '@flexible-reports/hooks/takt-logger/build-frb-takt-payload';
import { matchesFatalPatterns } from '@flexible-reports/utils/error-utils';

const metrics = new Map<string, { endpointName: string; initiatedAt?: number }>();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const metricsMiddleware: Middleware = (api: MiddlewareAPI) => next => (action: AnyAction) => {
  const apiAction = action as AnyApiAction<Arg>;
  const endpointName = apiAction.meta?.arg?.endpointName;
  const isSuccess = isFulfilled(action);
  const isFailure = isRejected(action);

  if (!endpointName || !((endpointName as ApiMapConfigKeys) in TaktApiMapConfig)) {
    return next(action);
  }

  if (isPending(action)) {
    metrics.set(apiAction.meta?.requestId, {
      endpointName,
      initiatedAt: apiAction.meta?.startedTimeStamp
    });
  }

  if (isFailure) {
    const rejectedAction = apiAction as RejectedAction<Arg>;
    if (matchesFatalPatterns(rejectedAction.payload?.status, rejectedAction.payload?.data)) {
      logger.logFatalMessage(`Api error ${rejectedAction.meta.arg.endpointName}`, { ...rejectedAction.error, ...rejectedAction.payload });
    } else {
      logger.logErrorMessage(`Api error ${rejectedAction.meta.arg.endpointName}`, { ...rejectedAction.error, ...rejectedAction.payload });
    }
  }

  if (isSuccess || isFailure) {
    const metric = metrics.get(apiAction.meta?.requestId);
    if (metric) {
      const apiConfig = TaktApiMapConfig[metric.endpointName as ApiMapConfigKeys];
      const startTime = metric.initiatedAt;
      const endTime = apiAction.meta?.fulfilledTimeStamp;

      const taktData = {
        context: `${TAKT_NAMESPACE_FOR_FRB}-${apiConfig.operation}`,
        details: JSON.stringify({
          event: { source: apiConfig.provider, eventName: apiConfig.operation },
          payload: isSuccess
            ? buildApiSuccessTaktPayload(metric.endpointName, startTime, endTime)
            : buildApiFailureTaktPayload(metric.endpointName, apiAction as RejectedAction<Arg>)
        } as TaktLoggerProps)
      };
      try {
        whenAmazonTakt(amazonTakt => amazonTakt.logCustom(taktData));
      } catch (error) {
        logger.logErrorMessage('There was an issue publishing takt event', error, taktData);
      }
      metrics.delete(action.meta.requestId);
    }
  }
  return next(action);
};
